<template>
	<div :class="{ dark: darkMode.enabled }">
		<div class="relative bg-ihh-white dark:selection:bg-gray-400 dark:text-gray-400 w-full min-h-screen font-gilroy">
			<Nav v-if="!hideSiteChrome" class="print:hidden" :class="{ 'mb-20 md:mb-0': store.bannerChecked && store.showBanner }" />

			<Breadcrumbs v-if="!hideSiteChrome" />

			<RouterView v-slot="{ Component, route }" @navHidden="navHidden = $event">
				<Suspense :timeout="200">
					<Component :is="Component" :key="route.meta.usePathKey ? route.path : undefined" />
					<template #fallback><spinner /></template>
				</Suspense>
			</RouterView>

			<Footer v-if="!hideSiteChrome" class="print:hidden" />

			<CartOfflineBanner />

			<CartValidation v-model:showCartValidationModal="showCartValidationModal" :oosItems="oosItems" :lowStockItems="lowStockItems" :invalidItems="invalidItems" />
		</div>
	</div>
</template>

<script>
import Logo from './components/partials/logo.vue'
import FooterLogo from './components/partials/footer-logo.vue'
import YMALBag from './components/pages/ihh-items/ymal-bag.vue'
import Shipping from './components/pages/ihh-items/shipping.vue'
import Nav from './components/layout/nav.vue'
import Breadcrumbs from './components/layout/breadcrumbs.vue'
import Footer from './components/layout/footer.vue'
import Mango from './helpers/mango'
import { captureWishlistError } from './helpers/sentry'
import { handleWishlistFailure } from './helpers/wishlistAuth.js'
import CartValidation from './components/layout/cart-validation.vue'
import CartOfflineBanner from './components/layout/cart-offline-banner.vue'
import { validateInventory } from './helpers/validateCart.js'
import { useCheckoutStore } from './stores/store'
import aa from 'search-insights'

export default {
	components: { Logo, FooterLogo, YMALBag, Shipping, Nav, Breadcrumbs, Footer, CartValidation, CartOfflineBanner },
	inject: ['store', 'darkMode'],
	data() {
		return {
			oosItems: [],
			lowStockItems: [],
			invalidItems: [],
			showCartValidationModal: false,
			checkout: useCheckoutStore(),
		}
	},
	watch: {
		loggedIn: {
			immediate: true,
			handler() {
				if (this.loggedIn) {
					this.fetchWishlists()
				}
			},
		},
		'store.cartInitialized': {
			immediate: true,
			handler() {
				if (this.store.cartInitialized) {
					console.log('Cart initialized')
					if (this.store.cart?.cartItems?.length) {
						this.validateCartInventory()
					}
				}
			},
		},
		hideMerchantWidget: {
			immediate: true,
			handler(hide) {
				document.body.classList.toggle('hide-merchant-widget', !!hide)
			},
		},
	},
	methods: {
		setRecentlyViewed() {
			let recentlyViewed = JSON.parse(sessionStorage.getItem('recentlyViewed')) || []
			this.store.recentlyViewed = recentlyViewed
		},
		async fetchWishlists() {
			try {
				let wishlists = await Mango.wishlists({ search: { author: this.store.user.id }, limit: 100 })
				if (Array.isArray(wishlists)) this.store.wishlists = wishlists
			} catch (error) {
				const signedOut = handleWishlistFailure(error, {
					store: this.store,
					capture: captureWishlistError,
					action: 'fetchWishlists',
				})
				if (!signedOut) return
				const path = this.$route?.path || ''
				if (path === '/account' || path.startsWith('/account/')) this.$router.push('/login')
			}
		},
		async validateCartInventory() {
			const { oosItems, lowStockItems, invalidItems } = await validateInventory()
			this.oosItems = oosItems
			this.lowStockItems = lowStockItems
			this.invalidItems = invalidItems

			if (this.oosItems.length > 0 || this.lowStockItems.length > 0 || this.invalidItems.length > 0) {
				this.showCartValidationModal = true
			}
		},
	},
	async mounted() {
		const guest = JSON.parse(sessionStorage.getItem('guest')) || {}
		if (Object.keys(guest).length) this.checkout.guest = guest

		const addresses = JSON.parse(sessionStorage.getItem('addresses')) || []
		if (addresses.length) this.checkout.addresses = addresses
		const shipping = JSON.parse(sessionStorage.getItem('shipping')) || {}
		if (Object.keys(shipping).length) this.checkout.shipping = shipping

		const billing = JSON.parse(sessionStorage.getItem('billing')) || {}
		if (Object.keys(billing).length) this.checkout.billing = billing
		this.setRecentlyViewed()

		// Set Algolia user token
		const memberId = window.localStorage.getItem('user')
		if (memberId) {
			aa('setAuthenticatedUserToken', memberId)
		}
	},
	computed: {
		loggedIn() {
			return this.store?.user?.id
		},
		hideSiteChrome() {
			return this.$route.matched.some((record) => record.meta.hideChrome)
		},
		hideMerchantWidget() {
			const path = this.$route.path || ''
			return path === '/account' || path.startsWith('/account/')
		},
	},
}
</script>
<style lang="postcss">
.gradient {
	@apply bg-gradient-to-b from-ihh-pink to-ihh-purple;
}
@font-face {
	font-family: 'Gilroy';
	src: url('https://cdn11.bigcommerce.com/s-nuj3c/content/fonts/gilroy-regular.woff') format('woff');
	font-weight: 400;
	font-style: normal;
}
@font-face {
	font-family: 'Gilroy';
	src: url('https://cdn11.bigcommerce.com/s-nuj3c/content/fonts/gilroy-medium.woff') format('woff');
	font-weight: 500;
	font-style: normal;
}
@font-face {
	font-family: 'Gilroy';
	src: url('https://cdn11.bigcommerce.com/s-nuj3c/content/fonts/gilroy-semibold.woff') format('woff');
	font-weight: 600;
	font-style: normal;
}
@font-face {
	font-family: 'Gilroy';
	src: url('https://cdn11.bigcommerce.com/s-nuj3c/content/fonts/gilroy-bold.woff') format('woff');
	font-weight: 700;
	font-style: normal;
}

#chat-button {
	@apply !z-20;
}

/* For most browsers */
input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* For Firefox */
input[type='number'] {
	-moz-appearance: textfield;
}

.prose > h1,
.prose > h2,
.prose > h3 {
	color: #494949;
}

#google-merchantwidget-iframe-wrapper {
	margin-top: 10px; /* Adds space above */
	margin-bottom: 10px; /* Adds space below */
}

body.hide-merchant-widget #google-merchantwidget-iframe-wrapper {
	display: none !important;
}

#___ratingbadge_0 {
	margin-top: 10px; /* Adds space above */
	margin-bottom: 10px; /* Adds space below */
}
</style>
